import { useState, useMemo } from 'react';
import { useStats, getDateRangeFromPreset, type DateRangePreset } from '../hooks/useStats';
import { DurationStats, CostStats, CompletionChart, ErrorAnalysis, EndpointSuccessChart, EndpointDurationChart } from '../components/stats';
import type { DateRange } from '../types';
import { format } from 'date-fns';

export function StatsPage() {
  const [selectedPreset, setSelectedPreset] = useState<DateRangePreset>('last7days');
  const [customRange, setCustomRange] = useState<DateRange>({ from: null, to: null });
  const [compareVersions, setCompareVersions] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);

  // Get the active date range
  const dateRange = useMemo(() => {
    if (selectedPreset === 'custom') {
      return customRange;
    }
    return getDateRangeFromPreset(selectedPreset);
  }, [selectedPreset, customRange]);

  // Fetch statistics
  const { stats, loading, error } = useStats(dateRange);

  // Filter stats by version if selected
  const displayStats = useMemo(() => {
    if (!stats) return null;
    if (!selectedVersion || !compareVersions) return stats;

    const versionData = stats.versionStats.find((v) => v.version === selectedVersion);
    if (!versionData) return stats;

    return {
      ...stats,
      duration: versionData.duration,
      cost: versionData.cost,
      completion: versionData.completion,
      errors: versionData.errors,
      endpointStats: versionData.endpointStats,
    };
  }, [stats, selectedVersion, compareVersions]);

  const hasMultipleVersions = (stats?.versions.length ?? 0) > 1;

  return (
    <div className="flex flex-col gap-3">
      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-200 p-3 shadow-soft flex-shrink-0">
        <div className="flex flex-wrap items-center gap-3">
          {/* Date Range Presets */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-surface-600">Range:</span>
            <div className="flex rounded border border-surface-200 overflow-hidden">
              {(['today', 'last7days', 'last30days', 'custom'] as DateRangePreset[]).map((preset) => (
                <button
                  key={preset}
                  onClick={() => setSelectedPreset(preset)}
                  className={`px-3 py-1 text-xs font-medium transition-all duration-200 ${
                    selectedPreset === preset
                      ? 'bg-indigo-50 text-indigo-600'
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {preset === 'today' && 'Today'}
                  {preset === 'last7days' && '7 Days'}
                  {preset === 'last30days' && '30 Days'}
                  {preset === 'custom' && 'Custom'}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Date Inputs */}
          {selectedPreset === 'custom' && (
            <div className="flex items-center gap-1">
              <input
                type="date"
                value={customRange.from ? format(customRange.from, 'yyyy-MM-dd') : ''}
                onChange={(e) => setCustomRange(prev => ({
                  ...prev,
                  from: e.target.value ? new Date(e.target.value) : null
                }))}
                className="px-2 py-1 text-xs border border-surface-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <span className="text-surface-400 text-xs">to</span>
              <input
                type="date"
                value={customRange.to ? format(customRange.to, 'yyyy-MM-dd') : ''}
                onChange={(e) => setCustomRange(prev => ({
                  ...prev,
                  to: e.target.value ? new Date(e.target.value + 'T23:59:59') : null
                }))}
                className="px-2 py-1 text-xs border border-surface-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          )}

          {/* Version Comparison Toggle */}
          {hasMultipleVersions && (
            <>
              <div className="h-4 w-px bg-surface-200" />
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={compareVersions}
                    onChange={(e) => {
                      setCompareVersions(e.target.checked);
                      if (!e.target.checked) setSelectedVersion(null);
                    }}
                    className="w-3 h-3 rounded border-surface-300 text-primary-500 focus:ring-primary-500"
                  />
                  <span className="text-xs font-medium text-surface-600">By Version</span>
                </label>
                {compareVersions && (
                  <select
                    value={selectedVersion || ''}
                    onChange={(e) => setSelectedVersion(e.target.value || null)}
                    className="px-2 py-1 text-xs border border-surface-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="">All</option>
                    {stats?.versions.map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                )}
              </div>
            </>
          )}

          {/* Stats Summary */}
          {stats && (
            <div className="ml-auto flex items-center gap-3 text-xs text-surface-500">
              <span>
                <strong className="text-surface-700">{stats.totalJobs}</strong> scans
              </span>
              {stats.versions.length > 0 && (
                <span>
                  <strong className="text-surface-700">{stats.versions.length}</strong> ver{stats.versions.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-3 p-2 rounded bg-danger-light border border-danger/30 text-danger-dark text-xs flex items-center gap-2 flex-shrink-0">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <svg className="w-10 h-10 animate-spin text-primary-500 mx-auto mb-3" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <p className="text-surface-500">Loading statistics...</p>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      {displayStats && !loading && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 auto-rows-[22rem]">
          {/* Row 1 */}
          <DurationStats
            stats={displayStats.duration}
            title={selectedVersion ? `Duration - ${selectedVersion}` : 'Duration Analysis'}
          />
          <CostStats
            stats={displayStats.cost}
            title={selectedVersion ? `Cost - ${selectedVersion}` : 'Cost Analysis'}
          />
          <EndpointSuccessChart
            stats={displayStats.endpointStats}
            title={selectedVersion ? `Endpoint Success - ${selectedVersion}` : 'Endpoint Success Rate'}
          />

          {/* Row 2 */}
          <CompletionChart
            stats={displayStats.completion}
            title={selectedVersion ? `Completion - ${selectedVersion}` : 'Completion Rate'}
          />
          <ErrorAnalysis
            errors={displayStats.errors}
            title={selectedVersion ? `Errors - ${selectedVersion}` : 'Error Analysis'}
          />
          <EndpointDurationChart
            stats={displayStats.endpointStats}
            title={selectedVersion ? `Endpoint Duration - ${selectedVersion}` : 'Endpoint Avg Duration'}
          />
        </div>
      )}

      {/* Version Comparison Table */}
      {stats && hasMultipleVersions && compareVersions && !selectedVersion && !loading && (
        <div className="mt-6 bg-white rounded-xl border border-surface-200 p-6 shadow-card">
          <h2 className="text-lg font-semibold text-surface-800 mb-4">Version Comparison</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-surface-600">Version</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-surface-600">Scans</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-surface-600">Success Rate</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-surface-600">Avg Duration</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-surface-600">Total Cost</th>
                </tr>
              </thead>
              <tbody>
                {stats.versionStats.map((v) => (
                  <tr key={v.version} className="border-b border-surface-100 hover:bg-surface-50">
                    <td className="py-3 px-4">
                      <span className="font-mono text-sm text-surface-800">{v.version}</span>
                    </td>
                    <td className="py-3 px-4 text-right text-sm text-surface-700">{v.jobCount}</td>
                    <td className="py-3 px-4 text-right">
                      <span className={`text-sm font-medium ${
                        v.completion.completedPercent >= 90 ? 'text-success-dark' :
                        v.completion.completedPercent >= 70 ? 'text-warning-dark' :
                        'text-danger-dark'
                      }`}>
                        {v.completion.completedPercent.toFixed(1)}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right text-sm text-surface-700">
                      {v.duration.average.toFixed(1)}s
                    </td>
                    <td className="py-3 px-4 text-right text-sm text-surface-700">
                      ${v.cost.total.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && !stats && selectedPreset === 'custom' && (!customRange.from || !customRange.to) && (
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <svg className="w-16 h-16 mx-auto mb-4 text-surface-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-surface-500 mb-2">Select a date range to view statistics</p>
            <p className="text-sm text-surface-400">Choose start and end dates above</p>
          </div>
        </div>
      )}

      {/* No Data State */}
      {!loading && !error && stats && stats.totalJobs === 0 && (
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <svg className="w-16 h-16 mx-auto mb-4 text-surface-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-surface-500 mb-2">No scan data found for this period</p>
            <p className="text-sm text-surface-400">Try selecting a different date range</p>
          </div>
        </div>
      )}

    </div>
  );
}
